/**
 * Task Debug Panel
 *
 * Development-focused panel for inspecting task details.
 * Shows raw task data including internal IDs, context, and timestamps.
 * Only visible when debug mode is enabled.
 *
 * Feature: 078-user-tasks
 *
 * @module components/composed/TaskDebugPanel
 */

import type { Task } from '@/hooks/graphql/useTaskMutationsGraphQL';

export interface TaskDebugPanelProps {
  task: Task;
  className?: string;
}

/**
 * Debug panel showing detailed task information for development
 */
export function TaskDebugPanel({ task, className = '' }: TaskDebugPanelProps) {
  return (
    <div className={`mt-3 p-3 bg-surface-2 border border-border/50 rounded text-xs font-mono ${className}`}>
      <div className="text-amber-500 font-semibold mb-2">Debug Info</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <span className="text-muted">id:</span>
        <span className="text-foreground">{task.id}</span>

        <span className="text-muted">actionType:</span>
        <span className="text-foreground">{task.actionType}</span>

        <span className="text-muted">targetType:</span>
        <span className="text-foreground">{task.targetType}</span>

        <span className="text-muted">targetId:</span>
        <span className="text-foreground">{task.targetId}</span>

        <span className="text-muted">targetUrl:</span>
        <span className="text-foreground break-all">{task.targetUrl}</span>

        {task.targetContext && (
          <>
            <span className="text-muted">targetContext:</span>
            <span className="text-foreground break-all">
              {JSON.stringify(task.targetContext)}
            </span>
          </>
        )}

        {task.targetType === 'photo_in_critique' && task.targetContext && 'photoId' in task.targetContext && (
          <>
            <span className="text-muted">→ critiqueSessionId:</span>
            <span className="text-cyan-400">{task.targetId}</span>

            <span className="text-muted">→ photoId:</span>
            <span className="text-cyan-400">{String(task.targetContext.photoId)}</span>
          </>
        )}

        <span className="text-muted">sourceType:</span>
        <span className="text-foreground">{task.sourceType}</span>

        {task.sourceId && (
          <>
            <span className="text-muted">sourceId:</span>
            <span className="text-foreground">{task.sourceId}</span>
          </>
        )}

        <span className="text-muted">status:</span>
        <span className="text-foreground">{task.status}</span>

        {task.hasProgress && (
          <>
            <span className="text-muted">progress:</span>
            <span className="text-foreground">
              {task.progressCurrent}/{task.progressRequired ?? '∞'}
            </span>
          </>
        )}

        <span className="text-muted">createdAt:</span>
        <span className="text-foreground">{task.createdAt}</span>

        {task.completedAt && (
          <>
            <span className="text-muted">completedAt:</span>
            <span className="text-foreground">{task.completedAt}</span>
          </>
        )}
      </div>
    </div>
  );
}
